import React, { useCallback, useState } from 'react';
import { Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import {
  ICustomer,
  ISearchCustomerReq,
  ISearchCustomerRes,
} from '@/interfaces/customer';
import { AxiosResponse } from 'axios';
import { searchCustomers } from '@/adapters/customer';
import { debounce } from 'lodash';
import { twMerge } from 'tailwind-merge';

interface IProps {
  value?: number;
  customer?: ICustomer;
  size?: SizeType;
  isAll?: boolean;
  handleSelectData: (customer: ICustomer) => void;
  readonly?: boolean;
  customClass?: string;
  allowClear?: boolean;
}

const _criteria: ISearchCustomerReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 20,
  pageStart: 1,
  search: '',
};

const CustomerSelector = ({
  value,
  size = 'large',
  handleSelectData,
  isAll = false,
  readonly = false,
  customer,
  customClass,
  allowClear = true,
}: IProps) => {
  const [criteria, setCriteria] = useState<ISearchCustomerReq>({
    ..._criteria,
  });

  const { data: customers, isLoading } = useQuery<
    AxiosResponse<ISearchCustomerRes>,
    Error
  >({
    queryKey: ['search-customer', criteria],
    queryFn: async () => await searchCustomers(criteria),
  });

  let groupOptions =
    customers?.data?.data?.map(g => {
      return {
        value: g.id,
        label: `${g.code} - ${g.name}`,
      };
    }) || [];

  const allOptions = [{ value: '', label: 'ทั้งหมด' }, ...groupOptions];

  const debouncedSearch = useCallback(
    debounce(value => {
      setCriteria({
        ...criteria,
        search: value,
      });
    }, 300),
    [],
  );

  const onChange = (value: number) => {
    const customerInfo = customers?.data?.data?.find(d => d.id == value);
    handleSelectData(customerInfo as ICustomer);
  };

  const initOptions = {
    value: customer?.id || 0,
    label: `${customer?.code} - ${customer?.name}`,
  };

  if (customer) groupOptions = [...groupOptions, initOptions];
  return (
    <Select
      allowClear={allowClear}
      showSearch
      onSearch={debouncedSearch}
      loading={isLoading}
      value={value}
      className={twMerge('md:min-w-[150px] w-full', customClass)}
      size={size}
      placeholder="เลือกลูกค้า"
      onChange={value => onChange(value)}
      onClear={() => onChange(0)}
      options={isAll ? allOptions : [...groupOptions]}
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      disabled={readonly}
    />
  );
};

export default CustomerSelector;
